import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { CommandBlock } from "@/components/CommandBlock";
import { SetupStep } from "@/components/SetupStep";
import { useConfirm } from "@/components/ConfirmDialog";
import { LoadError } from "@/components/LoadError";
import { Select } from "@/components/Select";
import { SettingsSection, SettingsSkeleton } from "./SettingsSection";
import { TextInput } from "@/components/TextInput";
import { oidcConfig } from "@/config";
import { Toast, useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";
import { isConfigured, isEnabled } from "@/lib/enums";
import {
  providerLabels,
  providerOptions,
  type SsoDomainVerification,
  type SsoMetadata,
  type SsoStatus,
} from "./shared";

export function SsoSettings({
  onEnabledChange,
}: {
  onEnabledChange: (enabled: boolean, idpProviderHint: string | null) => void;
}) {
  const { accessToken, user } = useAuth();
  const api = useApi();
  const { toast, showToast } = useToast();
  const { orgId } = useParams();
  const [status, setStatus] = useState<SsoStatus | null>(null);
  const [metadata, setMetadata] = useState<SsoMetadata | null>(null);
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const [verification, setVerification] =
    useState<SsoDomainVerification | null>(null);
  const [verificationLoaded, setVerificationLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [emailDomain, setEmailDomain] = useState("");
  const [idpMetadataUrl, setIdpMetadataUrl] = useState("");
  const [idpMetadataXml, setIdpMetadataXml] = useState("");
  const [metadataSource, setMetadataSource] = useState<"url" | "xml">("url");
  const [idpProviderHint, setIdpProviderHint] = useState<string>(
    providerOptions[0],
  );
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { copiedField, copyToClipboard } = useCopyToClipboard();

  const fetchStatus = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    return api.get<SsoStatus>(`/organizations/${orgId}/sso`);
  }, [accessToken, api, orgId]);

  const applyStatus = useCallback(
    (data: SsoStatus | null) => {
      if (data) {
        setStatus(data);
        setError(null);
        onEnabledChange(
          isConfigured(data.status) && isEnabled(data.status),
          data.idpProviderHint,
        );
      } else {
        setError("Couldn't load SSO status.");
      }
    },
    [onEnabledChange],
  );

  useEffect(() => {
    let cancelled = false;

    void fetchStatus().then((data) => {
      if (!cancelled) {
        applyStatus(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchStatus, applyStatus]);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    void api
      .get<SsoMetadata>(`/organizations/${orgId}/sso/metadata`)
      .then((data) => {
        if (!cancelled) {
          setMetadata(data);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMetadataLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, orgId]);

  const fetchVerification = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    return api.get<SsoDomainVerification>(
      `/organizations/${orgId}/sso/domain-verification`,
    );
  }, [accessToken, api, orgId]);

  useEffect(() => {
    if (!isConfigured(status?.status ?? 0)) {
      return;
    }

    let cancelled = false;

    void fetchVerification()
      .then((data) => {
        if (!cancelled) {
          setVerification(data);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setVerificationLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fetchVerification, status?.status]);

  async function handleVerifyDomain() {
    if (!accessToken || !orgId) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.send(
        `/organizations/${orgId}/sso/domain-verification`,
        "POST",
        undefined,
        "Couldn't verify the domain.",
      );

      setVerification(await fetchVerification());
      showToast("Domain verified.", "success");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Couldn't verify the domain.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.send(
        `/organizations/${orgId}/sso`,
        "POST",
        metadataSource === "url"
          ? { emailDomain, idpProviderHint, idpMetadataUrl }
          : { emailDomain, idpProviderHint, idpMetadataXml },
        "Couldn't configure SSO.",
      );

      applyStatus(await fetchStatus());
      showToast("SSO configured.", "success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't configure SSO.");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleEnabled() {
    if (!accessToken || !orgId || !status) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.send(
        `/organizations/${orgId}/sso/${isEnabled(status.status) ? "disable" : "enable"}`,
        "PATCH",
        undefined,
        "Couldn't update SSO status.",
      );

      applyStatus(await fetchStatus());
      showToast(
        isEnabled(status.status) ? "SSO disabled." : "SSO enabled.",
        "success",
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Couldn't update SSO status.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!accessToken || !orgId) {
      return;
    }

    const confirmed = await confirm(
      "Are you sure you want to delete this SSO connection?",
      {
        title: "Delete connection",
        confirmLabel: "Delete",
      },
    );

    if (!confirmed) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.send(
        `/organizations/${orgId}/sso`,
        "DELETE",
        undefined,
        "Couldn't delete SSO connection.",
      );

      applyStatus(await fetchStatus());
      showToast("SSO connection deleted.", "success");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Couldn't delete SSO connection.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const failed = Boolean(error) && !status;

  const loading =
    !failed &&
    (!status ||
      !metadataLoaded ||
      (isConfigured(status.status) && !verificationLoaded));

  return (
    <SettingsSection
      title="Single sign-on (SSO)"
      description="Let users sign in with your existing identity provider."
    >
      {failed ? (
        <LoadError
          what="single sign-on"
          onRetry={() => void fetchStatus().then(applyStatus)}
        />
      ) : loading || !status ? (
        <SettingsSkeleton />
      ) : (
        <>
          {error && (
            <p className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <div className="rounded-lg bg-surface p-4">
            <ol className="list-none">
              <SetupStep
                label="1"
                state={isConfigured(status.status) ? "done" : "current"}
                title="Configure your identity provider"
              >
                {!isConfigured(status.status) ? (
                  <>
                    <p className="mt-0.5 mb-3 text-sm text-foreground/70">
                      Add these values to your identity provider, then enter the
                      metadata URL it provides.
                    </p>
                    <div className="mb-2">
                      <label className="mb-1 block text-xs text-foreground">
                        Entity ID
                      </label>
                      <CommandBlock
                        command={metadata?.spEntityId ?? "Loading..."}
                        copyValue={metadata?.spEntityId ?? ""}
                        field="entityId"
                        wrap
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-foreground">
                        ACS URL
                      </label>
                      <CommandBlock
                        command={metadata?.spAcsUrl ?? "Loading..."}
                        copyValue={metadata?.spAcsUrl ?? ""}
                        field="acsUrl"
                        wrap
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <form onSubmit={handleCreate} className="mt-4 space-y-4">
                      <div>
                        <label
                          htmlFor="emailDomain"
                          className="mb-1.5 block text-sm text-foreground"
                        >
                          Email domain
                        </label>
                        <TextInput
                          type="text"
                          id="emailDomain"
                          value={emailDomain}
                          onChange={(e) => setEmailDomain(e.target.value)}
                          placeholder="example.com"
                          required
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="idpProviderHint"
                          className="mb-1.5 block text-sm text-foreground"
                        >
                          Identity provider
                        </label>
                        <Select
                          id="idpProviderHint"
                          value={idpProviderHint}
                          onChange={(e) => {
                            setIdpProviderHint(e.target.value);
                            setMetadataSource(
                              e.target.value === "GoogleWorkspace"
                                ? "xml"
                                : "url",
                            );
                          }}
                          className="w-full"
                        >
                          {providerOptions.map((option) => (
                            <option key={option} value={option}>
                              {providerLabels[option]}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <label
                          htmlFor="metadataSource"
                          className="mb-1.5 block text-sm text-foreground"
                        >
                          Identity provider metadata
                        </label>
                        <Select
                          id="metadataSource"
                          value={metadataSource}
                          onChange={(e) =>
                            setMetadataSource(e.target.value as "url" | "xml")
                          }
                          className="w-full"
                        >
                          <option value="url">Metadata URL</option>
                          <option value="xml">Metadata XML</option>
                        </Select>
                      </div>
                      {metadataSource === "url" ? (
                        <div>
                          <label
                            htmlFor="idpMetadataUrl"
                            className="mb-1.5 block text-sm text-foreground"
                          >
                            Metadata URL
                          </label>
                          <TextInput
                            type="url"
                            id="idpMetadataUrl"
                            value={idpMetadataUrl}
                            onChange={(e) => setIdpMetadataUrl(e.target.value)}
                            placeholder="https://idp.example.com/metadata"
                            required
                            className="w-full"
                          />
                        </div>
                      ) : (
                        <div>
                          <label
                            htmlFor="idpMetadataXml"
                            className="mb-1.5 block text-sm text-foreground"
                          >
                            Metadata XML
                          </label>
                          <textarea
                            id="idpMetadataXml"
                            value={idpMetadataXml}
                            onChange={(e) => setIdpMetadataXml(e.target.value)}
                            placeholder="<md:EntityDescriptor ..."
                            required
                            rows={6}
                            autoComplete="off"
                            spellCheck={false}
                            className="w-full rounded-lg border border-muted bg-surface px-3 py-2 font-mono text-xs text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                          />
                        </div>
                      )}
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting ? "Configuring..." : "Configure SSO"}
                      </Button>
                    </form>
                  </>
                ) : (
                  <>
                    <p className="mt-0.5 text-sm text-foreground/70">
                      {status.idpProviderHint
                        ? (providerLabels[status.idpProviderHint] ??
                          status.idpProviderHint)
                        : "Unknown provider"}{" "}
                      · {status.emailDomain}
                    </p>

                    <div className="mt-3">
                      <label className="mb-1 block text-xs text-foreground">
                        Entity ID
                      </label>
                      <CommandBlock
                        command={metadata?.spEntityId ?? "Loading..."}
                        copyValue={metadata?.spEntityId ?? ""}
                        field="entityIdConfigured"
                        wrap
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className="mt-2">
                      <label className="mb-1 block text-xs text-foreground">
                        ACS URL
                      </label>
                      <CommandBlock
                        command={metadata?.spAcsUrl ?? "Loading..."}
                        copyValue={metadata?.spAcsUrl ?? ""}
                        field="acsUrlConfigured"
                        wrap
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className="mt-2">
                      <label className="mb-1 block text-xs text-foreground">
                        SSO login URL
                      </label>
                      <CommandBlock
                        command={`${oidcConfig.authority}/sso/${user?.orgSlug ?? ""}`}
                        field="ssoLoginUrl"
                        wrap
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                      />
                    </div>
                  </>
                )}
              </SetupStep>

              <SetupStep
                label="2"
                state={
                  !isConfigured(status.status)
                    ? "todo"
                    : verification?.verifiedAt != null
                      ? "done"
                      : "current"
                }
                title={
                  isConfigured(status.status)
                    ? `Verify you own ${status.emailDomain ?? "your domain"}`
                    : "Verify your domain"
                }
              >
                {!isConfigured(status.status) ? (
                  <p className="mt-0.5 text-sm text-foreground/70">
                    Confirm that your organization owns the domain used for SSO.
                  </p>
                ) : verification?.verifiedAt != null ? (
                  <p className="mt-0.5 text-sm text-foreground/70">
                    Verified on {formatDate(verification!.verifiedAt!)}.
                  </p>
                ) : (
                  <>
                    <p className="mt-0.5 mb-3 text-sm text-foreground/70">
                      Add this TXT record at your DNS provider, then verify.
                    </p>

                    <div className="mb-2">
                      <label className="mb-1 block text-xs text-foreground">
                        Record name
                      </label>
                      <CommandBlock
                        command={verification?.recordName ?? "Loading..."}
                        copyValue={verification?.recordName ?? ""}
                        field="recordName"
                        wrap
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-foreground">
                        Record value
                      </label>
                      <CommandBlock
                        command={verification?.recordValue ?? "Loading..."}
                        copyValue={verification?.recordValue ?? ""}
                        field="recordValue"
                        wrap
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                      />
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <Button
                        variant="primary"
                        onClick={handleVerifyDomain}
                        disabled={submitting || !verification}
                      >
                        {submitting ? "Verifying..." : "Verify domain"}
                      </Button>
                      <span className="text-xs text-foreground/70">
                        DNS changes can take a few minutes to propagate.
                      </span>
                    </div>
                  </>
                )}
              </SetupStep>

              <SetupStep
                label="3"
                state={
                  isEnabled(status.status)
                    ? "done"
                    : verification?.verifiedAt != null
                      ? "current"
                      : "todo"
                }
                title="Enable SSO"
                last
              >
                {verification?.verifiedAt != null ? (
                  <div className="mt-2 flex flex-wrap gap-3">
                    <Button
                      variant="secondary"
                      onClick={toggleEnabled}
                      disabled={submitting}
                    >
                      {isEnabled(status.status) ? "Disable SSO" : "Enable SSO"}
                    </Button>
                  </div>
                ) : (
                  <p className="mt-0.5 text-sm text-foreground/70">
                    Once enabled, people can sign in through your identity
                    provider.
                  </p>
                )}
              </SetupStep>
            </ol>
          </div>

          {isConfigured(status.status) && (
            <div className="mt-4 flex items-center justify-between gap-6 rounded-lg border border-danger/30 p-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Delete this connection
                </h3>
                <p className="mt-0.5 max-w-md text-xs text-foreground/70">
                  Deleting this connection will remove the identity provider.
                  Members will sign in with their email and password instead.
                </p>
              </div>
              <Button
                variant="dangerOutline"
                onClick={handleDelete}
                disabled={submitting}
                className="shrink-0"
              >
                Delete connection
              </Button>
            </div>
          )}
        </>
      )}

      {confirmDialog}
      <Toast toast={toast} />
    </SettingsSection>
  );
}
